import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import { APPCONFIG } from '../config';
import { EasyMarineHttpClient } from '../_utils/easy-marine-http-client';

/** GET /api-support-ai/secure/me (CONTRATOS.md §4; `dashboards`: INSTRUCAO-DASHBOARDS D-6). */
export interface EasymarineAiMe {
    userId: number;
    email: string;
    name: string;
    supervisor: boolean;
    /** Os Dashboards estão ligados no backend (EASYMARINE_SUPPORT_AI_DASHBOARDS_ENABLED); ausente = desligados. */
    dashboards?: boolean;
}

/** As páginas cheias da tela: Conversas, Supervisão e Dashboards. */
export type PaginaDaTela = 'conversas' | 'supervisao' | 'dashboards';

/** Estado que cada tela (página ou cortina) mandou por último. */
interface EstadoDaTela {
    janela: Window;
    respondendo: boolean;
    naoLidas: number;
}

/** Os iframes da tela: o da página cheia e o da cortina. Só deles a casca aceita mensagem. */
const IFRAMES_DA_TELA = 'iframe.easymarine-ai-iframe, iframe.easymarine-ai-cortina-iframe';

/**
 * Casca do EasyMarine AI no admin (CONTRATOS.md §5). A tela é o app React em /ia/, na MESMA origem do admin,
 * aberta num iframe (página cheia ou cortina). Este serviço:
 * - confere se a tela existe neste ambiente (/ia/healthz responde "easymarine-ai"): sem ela, o nginx do admin
 *   devolveria o próprio index.html para /ia/, e nem o menu nem o ícone aparecem;
 * - pergunta ao backend se o usuário é supervisor (o item Supervisão do menu só aparece para ele) e se os Dashboards
 *   estão ligados (o item Dashboards só aparece com a tela no ambiente E o `dashboards: true` do GET /me, D-6);
 * - abre e fecha a cortina e avisa a tela quando ela fica visível ou escondida;
 * - escuta as mensagens da tela (só dos iframes dela e da mesma origem): estado (anel e bolinha do ícone do
 *   cabeçalho), sessão expirada (vai para o login) e abrir a página cheia;
 * - zera tudo no login e no logout (outro usuário pode entrar sem recarregar a página).
 */
@Injectable()
export class EasymarineAiService {
    static readonly FONTE_TELA = 'easymarine-ai';
    static readonly FONTE_CASCA = 'easymarine-admin';

    /** Endereço da tela (o ALB manda /ia/* para o serviço próprio dela). */
    readonly enderecoDaTela = '/ia/';

    /** A tela existe neste ambiente (conferido em /ia/healthz). */
    disponivel = false;
    /** A conferência já respondeu (com a tela ou sem ela). */
    telaConferida = false;
    /** Alguma tela está recebendo resposta da IA (anel no ícone do cabeçalho). */
    respondendo = false;
    /** Respostas que terminaram sem o atendente estar vendo (bolinha no ícone com a cortina fechada). */
    naoLidas = 0;
    cortinaAberta = false;
    /** A cortina já foi aberta alguma vez: o iframe só carrega a partir daí. */
    cortinaCarregada = false;
    supervisor = false;
    /** Os Dashboards estão ligados no backend (GET /me): o item do menu só aparece com eles (D-6). */
    dashboards = false;

    /** Pedido de abrir uma conversa na página cheia que já está aberta (a mesma URL não navega no Angular 4). */
    readonly paginaPedida = new Subject<string>();

    private estados: EstadoDaTela[] = [];
    private iframeDaCortina: HTMLIFrameElement = null;
    private conferindo = false;
    private meCarregado = false;
    private sessaoExpiradaEm = 0;

    constructor(private http: EasyMarineHttpClient, private router: Router, private zone: NgZone) {
        // fora da zona: só a mensagem válida da tela dispara a detecção de mudanças do Angular
        this.zone.runOutsideAngular(() => window.addEventListener('message', (evento: MessageEvent) => {
            if (this.mensagemDaTela(evento)) {
                this.zone.run(() => this.aoReceber(evento));
            }
        }));
        this.router.events.subscribe(e => {
            if (e instanceof NavigationEnd) {
                if (e.urlAfterRedirects.indexOf('/extra') === 0) {
                    this.zerar(); // login ou logout
                }
                // a página cheia saiu da tela: o estado dela (anel, bolinha) deixa de contar
                setTimeout(() => this.recalcular(), 0);
            }
        });
    }

    /** Chamado quando o menu aparece: confere a tela (uma vez) e, com ela, o GET /me (supervisor e Dashboards). */
    iniciar(): void {
        this.conferirTela();
        if (this.disponivel) {
            this.carregarSupervisor();
        }
    }

    /** Pergunta ao backend uma vez por login; erro (IA desligada, sem permissão) = nem supervisor nem Dashboards. */
    carregarSupervisor(): void {
        if (this.meCarregado || !localStorage.getItem('currentUser')) {
            return;
        }
        this.meCarregado = true;
        this.http.get<EasymarineAiMe>(APPCONFIG.apiHost + '/api-support-ai/secure/me').subscribe(
            (me: EasymarineAiMe) => {
                this.supervisor = !!(me && me.supervisor === true);
                this.dashboards = !!(me && me.dashboards === true);
            },
            () => {
                this.supervisor = false;
                this.dashboards = false;
                this.meCarregado = false; // tenta de novo na próxima vez que o menu aparecer
            }
        );
    }

    alternarCortina(): void {
        this.definirCortina(!this.cortinaAberta);
    }

    fecharCortina(): void {
        this.definirCortina(false);
    }

    definirCortina(aberta: boolean): void {
        if (aberta && !this.disponivel) {
            return;
        }
        if (aberta) {
            this.cortinaCarregada = true;
        }
        this.cortinaAberta = aberta;
        const corpo = document.getElementById('body') || document.body;
        if (aberta) {
            corpo.classList.add('quickview-open-easymarine-ai');
        } else {
            corpo.classList.remove('quickview-open-easymarine-ai');
        }
        this.avisarVisibilidade();
    }

    /** O iframe da cortina carregou (ou recarregou): a tela precisa saber se está visível. */
    registrarCortina(iframe: HTMLIFrameElement): void {
        this.iframeDaCortina = iframe;
        this.avisarVisibilidade();
    }

    /** Endereço do iframe da página cheia: Conversas, com a conversa pedida, a Supervisão ou os Dashboards. */
    enderecoDaPagina(pagina: PaginaDaTela, conversa: string): string {
        if (pagina === 'supervisao') {
            return this.enderecoDaTela + 'supervisao';
        }
        if (pagina === 'dashboards') {
            return this.enderecoDaTela + 'dashboards';
        }
        return conversa ? this.enderecoDaTela + '?conversa=' + encodeURIComponent(conversa) : this.enderecoDaTela;
    }

    private conferirTela(): void {
        if (this.telaConferida || this.conferindo) {
            return;
        }
        this.conferindo = true;
        const pedido = new XMLHttpRequest();
        pedido.open('GET', this.enderecoDaTela + 'healthz', true);
        pedido.onload = () => this.zone.run(() => {
            this.conferindo = false;
            this.telaConferida = true;
            // sem a imagem da tela, /ia/healthz devolve o index.html do próprio admin (200, mas outro corpo)
            this.disponivel = pedido.status === 200 && (pedido.responseText || '').trim() === 'easymarine-ai';
            if (this.disponivel) {
                this.carregarSupervisor();
            }
        });
        pedido.onerror = () => this.zone.run(() => {
            this.conferindo = false; // confere de novo na próxima vez que o menu aparecer
            this.disponivel = false;
        });
        pedido.send();
    }

    /** Login ou logout: nada do usuário anterior fica (supervisor, cortina, estado das telas). */
    private zerar(): void {
        this.supervisor = false;
        this.dashboards = false;
        this.meCarregado = false;
        this.fecharCortina();
        this.cortinaCarregada = false; // o iframe da cortina sai do DOM
        this.iframeDaCortina = null;
        this.estados = [];
        this.respondendo = false;
        this.naoLidas = 0;
    }

    private origem(): string {
        return window.location.protocol + '//' + window.location.host;
    }

    /** Mensagem da tela: mesma origem, formato da tela e vinda de um dos iframes dela (nunca de outra janela). */
    private mensagemDaTela(evento: MessageEvent): boolean {
        if (evento.origin !== this.origem()) {
            return false;
        }
        const dados = evento.data;
        if (!dados || typeof dados !== 'object' || dados.fonte !== EasymarineAiService.FONTE_TELA) {
            return false;
        }
        return this.janelaDaTela(evento.source as Window);
    }

    private janelaDaTela(janela: Window): boolean {
        if (!janela) {
            return false;
        }
        const iframes = document.querySelectorAll(IFRAMES_DA_TELA);
        for (let i = 0; i < iframes.length; i++) {
            if ((iframes[i] as HTMLIFrameElement).contentWindow === janela) {
                return true;
            }
        }
        return false;
    }

    private avisarVisibilidade(): void {
        const iframe = this.iframeDaCortina;
        if (!iframe || !iframe.contentWindow) {
            return;
        }
        iframe.contentWindow.postMessage(
            { fonte: EasymarineAiService.FONTE_CASCA, tipo: 'visibilidade', visivel: this.cortinaAberta },
            this.origem()
        );
    }

    private aoReceber(evento: MessageEvent): void {
        const dados = evento.data;
        if (dados.tipo === 'estado') {
            this.atualizarEstado(evento.source as Window, dados.respondendo === true, Number(dados.naoLidas) || 0);
        } else if (dados.tipo === 'sessao-expirada') {
            this.sessaoExpirada();
        } else if (dados.tipo === 'abrir-pagina') {
            this.abrirPagina(typeof dados.conversa === 'string' ? dados.conversa : '');
        }
    }

    /**
     * Da cortina para a página cheia. Com a página já aberta nessa mesma URL, o Angular 4 não navega: a página é
     * avisada (paginaPedida) e leva o iframe dela à conversa pedida.
     */
    private abrirPagina(conversa: string): void {
        this.fecharCortina();
        const destino = this.router.createUrlTree(['/app/easymarine-ai'], {
            queryParams: conversa ? { conversa: conversa } : {}
        });
        const mesmaUrl = this.router.url === this.router.serializeUrl(destino);
        this.router.navigateByUrl(destino).then(() => {
            if (mesmaUrl) {
                this.paginaPedida.next(conversa);
            }
        });
    }

    private atualizarEstado(janela: Window, respondendo: boolean, naoLidas: number): void {
        // uma tela por janela (página e cortina podem estar abertas juntas)
        this.estados = this.estados.filter(e => e.janela !== janela);
        this.estados.push({ janela: janela, respondendo: respondendo, naoLidas: Math.max(0, naoLidas) });
        this.recalcular();
    }

    /** Soma o estado das telas que ainda estão no DOM (a que saiu, por navegação, não conta mais). */
    private recalcular(): void {
        this.estados = this.estados.filter(e => !!e.janela && !e.janela.closed && this.janelaDaTela(e.janela));
        this.respondendo = this.estados.some(e => e.respondendo);
        this.naoLidas = this.estados.reduce((soma, e) => soma + e.naoLidas, 0);
    }

    /** O mesmo do interceptor do admin (401): avisa e vai para o login; uma vez só, mesmo com duas telas abertas. */
    private sessaoExpirada(): void {
        const agora = Date.now();
        if (agora - this.sessaoExpiradaEm < 10000) {
            return;
        }
        this.sessaoExpiradaEm = agora;
        this.fecharCortina();
        alert('Sua sessão expirou.');
        this.router.navigate(['/extra/login']);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/easymarine-ai/easymarine-ai.service.ts